<template>
    <div v-if="internalItem">
        <ModalMenuItemGallery
            ref="modalMenuItemGallery"
            :images="resources"
            :selected="internalItem.icon"
            @select="handleSelectIcon"
        />
        <div>
            <h3 v-if="action === 'create'">{{ $t('apps.configurator.manage-item.title') }}</h3>
            <h3 v-else>{{ internalItem.name }}</h3>
        </div>

        <!-- Main -->
        <div class="mt-12">
            <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.type_and_visibility') }}</h3>

            <div class="flex flex-col vx-card p-8">
                <div class="w-full">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.type') }}</LabelForm>
                    <vs-select
                        :disabled="readOnly || this.internalItem.submenu_items.length > 0"
                        class="w-96 border-grey-light"
                        v-model="internalItem.item_template_uuid"
                    >
                        <vs-select-item :key="templatesOpt.value" :value="templatesOpt.value" :text="templatesOpt.label" v-for="templatesOpt in templatesOpts"/>
                    </vs-select>

                    <Alert v-if="!readOnly && this.internalItem.submenu_items.length > 0" type="warning" class="mt-4">
                        {{ $t('apps.configurator.manage-item.cannot_change_template') }}
                    </Alert>
                </div>

                <div v-if="template">
                    <LabelForm font-size="text-sm" margins="mb-0 mt-3">{{ $t('apps.configurator.manage-item.name') }}</LabelForm>
                    <vs-input :disabled="readOnly" type="text" maxlength="50"
                              class="input-modal w-96 border-grey-light" v-model="internalItem.name" :danger="!internalItem.name"/>
                </div>

                <div>
                    <LabelForm font-size="text-sm" margins="mb-0 mt-3">{{ $t('apps.configurator.manage-item.published') }}</LabelForm>
                    <vs-select :disabled="readOnly" class="w-20 border-grey-light" v-model="internalItem.published" required>
                        <vs-select-item :key="publishedOpt.value" :value="publishedOpt.value" :text="publishedOpt.label" v-for="publishedOpt in publishedOpts"/>
                    </vs-select>
                </div>
            </div>
        </div>
        <!-- Main -->

        <!-- Button -->
        <div class="mt-12" v-if="template && hasAnyTemplatePresetAttributes(['icon', 'width'])">
            <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.button') }}</h3>

            <div class="vx-card p-8 flex flex-col gap-3">

                <div v-if="hasTemplatePresetAttribute('width')">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.columns') }}</LabelForm>
                    <vs-select :disabled="menu.type === 'tab' || readOnly" class="w-20 border-grey-light" v-model="internalItem.width" required>
                        <vs-select-item :key="columns" :value="columns" :text="columns" v-for="columns in columnsOptions"/>
                    </vs-select>
                </div>

                <div v-if="hasTemplatePresetAttribute('icon')" class="w-full">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.icon') }}</LabelForm>
                    <div class="flex flex-row items-center gap-2">
                        <vs-input type="text" maxlength="50"
                                  class="input-modal w-full border-grey-light"
                                  v-model="internalItem.icon"
                                  disabled
                        />
                        <div v-if="!readOnly" class="flex flex-row items-center gap-2">
                            <button class="sk-btn sk-btn-small sk-btn-primary rounded-lg btn-border p-2" @click="openGallery">
                                <feather-icon icon="ImageIcon" svgClasses="h-6 w-6 stroke-current text-primary" class="align-middle"/>
                            </button>
                            <button class="sk-btn sk-btn-small sk-btn-danger rounded-lg btn-border p-2" @click="clearIcon">
                                <feather-icon icon="XIcon" svgClasses="h-6 w-6 stroke-current text-danger" class="align-middle"/>
                            </button>
                        </div>
                    </div>

                    <div v-if="imagesObject">
                        <Alert
                            v-if="imagesObject.multiLanguage && imagesObject.missingImages.length > 0"
                            type="warning"
                            class="mt-4"
                        >
                            <p class="font-medium mb-2">{{ $t('apps.configurator.preview.image_missing') }}:</p>
                            <ul>
                                <li v-for="image in imagesObject.missingImages" :key="image">{{ image }}</li>
                            </ul>
                        </Alert>

                        <Gallery :asset-list="imagesObject.images" />
                    </div>
                </div>

                <div class="languages-inputs">
                    <LabelForm font-size="text-base" margins="mb-2">{{ $t('apps.configurator.manage-item.item_title') }}</LabelForm>
                    <DynamicForm
                        v-model="languageValue"
                        @input="getDinamicInputsLanguages"
                        :languages="languages.map(lang => lang.acronym)"
                        :columns=2
                        :isReadOnly="readOnly"
                    />
                </div>
            </div>
        </div>
        <!-- Button -->

        <!-- Configuration -->
        <div class="mt-12" v-if="showConfiguration">
            <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.configuration') }}</h3>

            <div class="vx-card p-8">
                <DynamicForm
                    v-model="configurationValue"
                    @input="getDinamicInputsConfiguration"
                    @refresh="refreshTemplate"
                    :languages="languages.map(lang => lang.acronym)"
                    :columns=1
                    :isReadOnly="readOnly"
                    :condensed-selects="!hasAnyTemplatePresetAttributes(['screen_name', 'flag', 'section'])"
                />
            </div>
        </div>
        <!-- Configuration -->

        <!-- Metadata -->
        <div class="mt-12" v-if="template && hasAnyTemplatePresetAttributes(['screen_name', 'flag', 'section'])">
            <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.metadata') }}</h3>

            <div class="vx-card p-8 flex flex-col gap-3">
                <div v-if="hasTemplatePresetAttribute('screen_name_category') && screenNameCategories.length > 0">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.screen-name-category') }}</LabelForm>
                    <vs-select :disabled="readOnly" class="w-full border-grey-light"
                               v-model="internalItem.screen_name_category" required>
                        <vs-select-item :key="category" :value="category" :text="category" v-for="category in screenNameCategories"/>
                    </vs-select>
                </div>

                <div v-if="hasTemplatePresetAttribute('screen_name')">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.screen-name') }}</LabelForm>
                    <vs-input :disabled="readOnly" type="text" maxlength="50"
                              class="input-modal w-full border-grey-light" v-model="internalItem.screen_name" />
                </div>

                <div v-if="hasTemplatePresetAttribute('flag')">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.flag') }}</LabelForm>
                    <vs-input disabled type="text" maxlength="50"
                              class="input-modal w-full border-grey-light" v-model="internalItem.flag" />
                </div>

                <div v-if="hasTemplatePresetAttribute('section')">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.section') }}</LabelForm>
                    <vs-input disabled type="text" maxlength="50" class="w-full border-grey-light input-modal"
                              v-model="internalItem.section" />
                </div>
            </div>
        </div>
        <!-- Metadata -->

        <b-alert :show="errorsOnSave.length > 0" variant="danger" class="mt-5 rounded-lg">
            <ul class="list-none">
                <li v-for="(errorString, index) in errorsOnSave" :key="index">{{ errorString }}</li>
            </ul>
        </b-alert>

        <div class="action-item-button-container vx-col w-full mt-8">
            <button @click.stop="cancel" class="xs:w-full sm:w-auto rounded-lg sk-btn sk-btn-primary btn-border mr-2">
                {{ $t("apps.configurator.menu-list.cancel") }}
            </button>
            <button v-if="!readOnly" @click.stop="save" :disabled="!isValid()" class="xs:w-full sm:w-auto rounded-lg sk-btn sk-btn-primary">
                {{ $t("apps.configurator.menu-list.save") }}
            </button>
        </div>
    </div>
</template>

<script>
import LabelForm from '@/modules/Shared/Components/form/LabelForm'
import DynamicForm from "@/modules/Shared/Components/form/DynamicForm";
import SKModal from "@/modules/Shared/Components/modal/SKModal.vue";
import { v4 as uuidv4 } from 'uuid';
import Gallery from "@/modules/Apps/Components/resources/gallery/Gallery.vue";
import emptyAlert from "@/modules/Shared/Components/alert/emptyAlert.vue";
import ModalMenuItemGallery from "@/modules/Apps/Components/configurator/ModalMenuItemGallery.vue";
import Alert from "@/modules/Shared/Components/alert/Alert.vue";
import ConfiguratorItemTemplateService from "@/modules/Apps/Services/Configurator/ConfiguratorItemTemplateService";
import loader from "@/modules/Shared/Mixins/loader";
import { mapGetters } from 'vuex'

export default {
    name: 'LegacyManageMenuItemForm',
    mixins: [loader],
    props: {
        menu: {
            type: Object,
            required: true
        },
        layout: {
            type: Object|null,
            required: true,
            default: null
        },
        templates: {
            type: Array,
            required: true
        },
        languages: {
            type: Array,
            required: true
        },
        resources: {
            type: Array,
            required: true
        },
        afterItemUuid: {
            type: String,
            required: false
        },
        insideItemUuid: {
            type: String,
            required: false
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    components: {
        Alert,
        ModalMenuItemGallery,
        emptyAlert,
        Gallery,
        SKModal,
        LabelForm,
        DynamicForm
    },
    watch: {
        'internalItem.item_template_uuid': {
            async handler(current, old) {
                if (!current || current === old || this.isInitializingFromUpdate) {
                    return
                }
                await this.changeTemplate(current)
            }
        }
    },
    methods: {
        isValid() {
            if (!this.template) {
                return false
            }

            const validCondiguration = this.template.configuration_template === null ||
                this.template.configuration_template
                    .filter(field => field.required)
                    .every(field => this.isValidConfigurationField(field))

            const validScreenNameCategory = !this.hasTemplatePresetAttribute('screen_name_category')
                || this.screenNameCategories.length === 0
                || !!this.internalItem.screen_name_category

            return validCondiguration && validScreenNameCategory && this.internalItem.name
        },
        isValidConfigurationField(field) {
            return this.configurationValue.values &&
                this.configurationValue.values.hasOwnProperty(field.key)
                && this.configurationValue.values[field.key] !== null
                && this.configurationValue.values[field.key] !== ''
        },
        titlesAreValid() {
            return this.languageValue.template
                .every(field =>
                    this.languageValue.values.hasOwnProperty(field.key) &&
                    this.languageValue.values[field.key] !== null &&
                    this.languageValue.values[field.key] !== ''
                )
        },
        clearIcon() {
            this.internalItem.icon = ''
            this.$emit('loaded')
        },
        handleSelectIcon(icon) {
            this.internalItem.icon = icon.key
            this.$emit('loaded')
        },
        openGallery() {
            this.$refs.modalMenuItemGallery.open()
        },
        internalSetItemsOrdering() {
            let ordering = 0

            const unparseItem = function (item) {
                return {
                    ...item,
                    ordering: ordering,
                    submenu_items: item.submenu_items.map((subitem) => unparseItem(subitem, ++ordering))
                }
            }

            this.menu.items = this.menu.items.map((item) => unparseItem(item, ++ordering))
        },
        async save() {
            if (!this.hasErrors()) {
                this.internalItem.configuration = this.configurationValue.values === null ? [] : this.configurationValue.values
                this.internalItem.titles = this.languageValue.values

                if (this.action === 'update') {
                    this.menu.items = this.replaceItem(this.menu.items, this.internalItem.uuid)
                } else if (this.insideItemUuid) {
                    this.menu.items = this.addItemInside(this.menu.items, this.insideItemUuid)
                } else if (this.afterItemUuid) {
                    this.menu.items = this.addItemAfter(this.menu.items, this.afterItemUuid)
                } else {
                    this.menu.items.push(this.internalItem)
                }

                this.internalSetItemsOrdering()
                this.$emit('change')
            }
        },
        cancel() {
            this.$emit('cancel')
        },
        addItemInside(items, itemUuid) {
            return this.findItem(items, itemUuid, (items, index) => {
                items[index].submenu_items.splice(0, 0, this.internalItem)
                return items
            })
        },
        addItemAfter(items, itemUuid) {
            return this.findItem(items, itemUuid, (items, index) => {
                items.splice(index + 1, 0, this.internalItem)
                return items
            })
        },
        replaceItem(items, itemUuid) {
            return this.findItem(items, itemUuid, (items, index) => {
                items.splice(index, 1, this.internalItem)
                return items
            })
        },
        findItem(items, itemUuid, callback) {
            const index = items.findIndex(item => item.uuid === itemUuid)
            if (index !== -1) {
                return callback(items, index)
            }
            return items.map(item => {
                item.submenu_items = this.findItem(item.submenu_items, itemUuid, callback)
                return item
            })
        },
        hasErrors() {
            this.errorsOnSave = []

            if (!this.internalItem.width) {
                this.internalItem.width = 1
            }

            if (!this.internalItem.section) {
                this.errorsOnSave = [...this.errorsOnSave, this.$t('apps.configurator.manage-menu.error-section')]
            }

            return this.errorsOnSave.length !== 0
        },
        async changeTemplate(templateUuid) {
            const template = await this.loadItemTemplate(templateUuid)

            this.configurationValue = template.configuration_template !== null && template.configuration_template !== false
                ? { template: template.configuration_template, values: template.configuration }
                : { values: {} }

            if (!template.preset.includes('icon')) {
                this.internalItem.icon = null
            }

            if (!this.internalItem.name) {
                this.internalItem.name = template.titles.en
            }

            this.internalItem.flag = template.flag
            this.internalItem.section = template.section
            this.resolveScreenNameCategory(template)

            if (!this.template) {
                this.languageValue.values = template.titles
                this.internalItem.icon = template.icon
                this.internalItem.screen_name = template.screen_name
            }

            this.template = template
            this.modifyItemWidthBasedOnTemplate()

            this.$emit('loaded')
        },
        // Catalogue a template offers, always as an array: the API models the
        // empty catalogue as NULL, so NULL and [] mean the same thing.
        templateScreenNameCategories(template) {
            return template && Array.isArray(template.screen_name_categories)
                ? template.screen_name_categories
                : []
        },
        // The category catalogue lives on the item template, so a template
        // change can leave the current value outside the new list. It is
        // resolved here the same way `icon` is reset when the new preset
        // drops it: kept when still valid, first value otherwise, and null
        // when the new template offers no categories at all.
        resolveScreenNameCategory(template) {
            const categories = template.preset.includes('screen_name_category')
                ? this.templateScreenNameCategories(template)
                : []

            if (categories.length === 0) {
                this.internalItem.screen_name_category = null
                return
            }

            if (!categories.includes(this.internalItem.screen_name_category)) {
                this.internalItem.screen_name_category = categories[0]
            }
        },
        async loadItemTemplate(itemTemplateUuid) {
            let template = null

            await this.loadAndIgnoreError(async () => {
                const resortUuid = this.menu.resorts.length ? this.menu.resorts[0] : null
                template = await ConfiguratorItemTemplateService.getItemTemplate(itemTemplateUuid, this.$route.params.uuid, resortUuid, this.$route.params.versionUuid)
            })

            return template
        },
        async refreshTemplate() {
            this.template = await this.loadItemTemplate(this.template.uuid)
            this.configurationValue.template = this.template.configuration_template
        },
        getDinamicInputsLanguages(dynamicInputs) {
            this.languageValue = dynamicInputs
        },
        getDinamicInputsConfiguration(dynamicInputs) {
            for (let key in dynamicInputs.values) {
                if (dynamicInputs.values.hasOwnProperty(key) && typeof dynamicInputs.values[key] === 'boolean') {
                    dynamicInputs.values[key] = dynamicInputs.values[key].toString();
                }
            }
            this.configurationValue = dynamicInputs
        },
        getImageAcronym(image) {
            const imageName = image.split('/').pop();
            const imageNameWithoutExtension = imageName.split('.')[0];
            const candidates = this.spotlioLanguages.filter(lang => imageNameWithoutExtension.endsWith(`_${lang.acronym}`))
            return candidates.length > 0 ? candidates[0].acronym : null
        },
        hasTemplatePresetAttribute(attribute) {
            if (!this.template) {
                return false
            }

            // Webview template should show/hide width attribute based on layout
            if (attribute === 'width' && !this.layout) {
                return false
            }

            return this.template.preset.includes(attribute)
        },
        hasAnyTemplatePresetAttributes(attributesArray) {
            return attributesArray.some(attribute => this.hasTemplatePresetAttribute(attribute))
        },
        modifyItemWidthBasedOnTemplate() {
            // Webview template should show/hide width attribute based on layout
            if (!this.layout) {
                return
            }

            if (!this.template.preset.includes('width')) {
                this.internalItem.width = this.layout.values.columns_number
            }
        },
        async initialize(item = null) {
            this.template = null
            this.errorsOnSave = []
            const userLang = this.$store.state.AppActiveUser.language

            this.languageValue = {
                template: this.languages.map(lang => ({
                    key: lang.acronym,
                    input: "textfield",
                    title: { [userLang]: this.spotlioLanguages.find(lang2 => lang2.acronym === lang.acronym).name },
                    required: true
                }))
            }

            if (!item) {
                this.action = 'create'
                this.configurationValue = { values: {} }
                this.internalItem = {
                    uuid: uuidv4(),
                    name: '',
                    configuration: null,
                    flag: '',
                    icon: '',
                    item_template_uuid: null,
                    published: true,
                    screen_name: '',
                    screen_name_category: null,
                    section: '',
                    submenu_items: [],
                    titles: {},
                    width: 1
                }
            } else {
                this.isInitializingFromUpdate = true
                this.action = 'update'
                this.internalItem = JSON.parse(JSON.stringify(item))

                this.template = await this.loadItemTemplate(this.internalItem.item_template_uuid)

                this.configurationValue = {
                    template: this.template.configuration_template,
                    values: this.internalItem.configuration
                }

                this.languageValue.values = this.internalItem.titles
                this.isInitializingFromUpdate = false
            }

            this.$emit('loaded');
        }
    },
    data() {
        return {
            isInitializingFromUpdate: false,
            action: 'create',
            internalItem: null,
            configurationValue: { values: {} },
            languageValue: {},
            errorsOnSave: [],
            template: null,
            publishedOpts: [
                { label: this.$i18n.t('yes'), value: true },
                { label: this.$i18n.t('no'), value: false },
            ],
            widthOpts: [
                { label: this.$t('apps.configurator.manage-item.with-1-column'), value: 1 },
                { label: this.$t('apps.configurator.manage-item.with-2-column'), value: 2 },
                { label: this.$t('apps.configurator.manage-item.with-3-column'), value: 3 },
            ],
        }
    },
    computed: {
        ...mapGetters(['spotlioLanguages']),
        screenNameCategories() {
            return this.templateScreenNameCategories(this.template)
        },
        columnsOptions() {
            return Array(this.layout.values.columns_number)
                .fill()
                .map((_, index) => index + 1)
        },
        imagesObject() {
            if (!this.internalItem || !this.internalItem.icon) {
                return null
            }

            const singleImageRegex = new RegExp(`/${this.internalItem.icon}\\.`)
            let images = this.resources.filter(resource => resource.match(singleImageRegex))

            if (images.length > 0) {
                return {
                    multiLanguage: false,
                    images: images
                }
            }

            const acronyms = this.languages.map(lang => lang.acronym)
            const multilanguageImageRegex = new RegExp(`/${this.internalItem.icon}_(${acronyms.join('|')})\\.`)
            images = this.resources.filter(resource => resource.match(multilanguageImageRegex))

            if (images.length > 0) {
                const missingLanguages = acronyms.filter(acronym => !images.map(this.getImageAcronym).includes(acronym))
                const extension = images[0].split('.').pop()
                return {
                    multiLanguage: true,
                    images: images,
                    missingImages: missingLanguages.map(acronym => `${this.internalItem.icon}_${acronym}.${extension}`),
                }
            }

            return null
        },
        templatesOpts() {
            if (this.templates !== null) {
                return this.templates.map(template => {
                    return {
                        label: template.name.scalar !== undefined
                            ? this.$t(template.name.scalar)
                            : template.name[this.$store.getters.applicationLanguage],
                        value: template.uuid
                    }
                }).sort((a, b) => {
                    return a.label < b.label ? -1 : a.label > b.label ? 1 : 0
                })
            }
        },
        showConfiguration() {
            if (!this.template) {
                return false
            }

            return this.template.configuration_template !== null
                && this.template.configuration_template.some(field => !field.hasOwnProperty('show') || field.show !== false)
        }
    }
}
</script>
